import { useEffect, useState } from "react";

const LOAD_MORE_ROOT_MARGIN = "120px";

interface UseInfiniteScrollOptions {
  /** Called when the sentinel scrolls into view. Omit to disable paging. */
  onLoadMore?: () => void;
  /** Whether another page is available. */
  hasMore: boolean;
  /** Whether a page fetch is currently in flight. */
  isLoading: boolean;
}

/**
 * Calls onLoadMore when a sentinel element scrolls into the surrounding list.
 * Observation is suspended while a fetch is in flight or no further pages exist,
 * so a single scroll to the bottom cannot fire overlapping requests.
 * @param options - The load handler and the current paging state
 * @returns A ref callback to attach to the sentinel element
 */
export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading,
}: UseInfiniteScrollOptions) {
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!sentinel || !onLoadMore || !hasMore || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore();
        }
      },
      {
        root: sentinel.closest("[cmdk-list]"),
        rootMargin: LOAD_MORE_ROOT_MARGIN,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, onLoadMore, hasMore, isLoading]);

  return setSentinel;
}
